/**
 * THE LAST LOOK — the reckoning a survey passes through before it can be sent
 * (**D-017**).
 *
 * The design authority, walkthrough round 3, 2026-08-06:
 *
 *   *"I just closed and submitted an incomplete survey… I should not have been
 *    able to submit with unanswered questions."*
 *
 * ── The tension this module exists to resolve, and it is a real one ──────────
 * "Refuse to submit when anything is missing" is the obvious fix and it is
 * WRONG. Specification §2.3 says in as many words that a survey with a genuine
 * gap still submits — *"If an answer physically can't be captured (locked room):
 * survey either stays open for a return trip OR submits with the gap flagged —
 * Chip/Tim decide on every gap"* — and §4 rule 8 says a missing recommended
 * photo NEVER blocks. A hard completeness gate strands a surveyor at a locked
 * pit at five o'clock with a day's work he cannot send.
 *
 * The line is not complete-versus-incomplete. It is **deliberate versus
 * accidental**:
 *
 *   deliberate — the surveyor stood there, could not get the answer, and said
 *                so. Must still submit, with the reason travelling to the office.
 *   accidental — the surveyor never saw the question. Must NOT go silently.
 *
 * So this module does not decide whether a survey is finished. It builds the
 * list of answers that are outstanding and NOT yet spoken for, and the close
 * screen withholds Submit until that list is empty. Nothing here removes a gap,
 * changes a gap's reason, or makes a survey submit cleaner than it is.
 *
 * ── ANSWERING is the only thing that empties it — round 7, D-038 ────────────
 * There used to be a second way: a collapsed drawer, a reason field, and a
 * confirm that marked the answer unavailable — an acknowledgement that settled
 * the row without an answer. The design authority, 2026-09-03:
 *
 *   *"Remove … the blocker entry option [the drawer's toggle]."*
 *   *"Ensure it walks users through every question they need to answer and
 *    allows them to correct it."*
 *
 * (Her words for the toggle are quoted verbatim in `tests/unit/word-diet.test.ts`
 * and banned from src/ui, comments included — a header that repeated them would
 * be the control's words back in the file that deleted it.)
 *
 * The §2.3 escape it existed for is NOT closed — it moved to the door every
 * other screen already has. A question nobody could get is answered No / 0,
 * and what stopped the surveyor is written in that section's notes (D-033 R15:
 * *"users can record any blockage in the Notes section at the bottom"*). The
 * office reads the note, the record still carries every gap the engines found,
 * and the surveyor is not asked to learn a second vocabulary at the door.
 *
 * `acknowledgeGap` is therefore gone from this module and so is the acknowledged
 * shape on `OutstandingItem`/`Reckoning`. `applyAcknowledgements` STAYS: records
 * acknowledged before today still carry their words, and the office must go on
 * reading them exactly as it did.
 *
 * ── What counts as outstanding, and the three categories ────────────────────
 *
 *  1. **`unanswered`** — a visible, required question with no answer. With the
 *     universal defaults of D-016 this is a small set: text, number, select,
 *     date, measure and ranges questions, which have no honest "no" to open on.
 *     That smallness is what makes reviewing them feasible at all.
 *
 *  2. **`untouched`** — a §4 arithmetic failure over answers the FORM wrote and
 *     the surveyor never touched. D-016 §3 changed an untouched disconnect pair
 *     from two `unanswered` gaps into one `validation_failed` gap reading
 *     *"Mainline disconnects: 0 + 0 = 0 — 6 cars unaccounted for"*. That is a
 *     stronger statement, but it is still a question nobody answered, and left
 *     out of this list it would be the same silent submission in a new costume.
 *     `defaultedAnswers` (D-016) is what tells the two apart: if the question is
 *     still carrying the form's own answer, nobody engaged with it.
 *
 *     A validation failure over answers the surveyor DID enter is deliberately
 *     NOT here. They were asked, they answered, the live chip told them the sum
 *     was wrong, and §2.3 leaves the decision with Chip and Tim. Demanding an
 *     acknowledgement for a number somebody typed on purpose would be the hard
 *     gate this module exists to avoid.
 *
 *  3. **`photo`** (Amendment 8, D-041, round 9 task 4) — a REQUIRED shot the
 *     survey needs, not taken. §4 rule 8's "warn, never block" is for
 *     RECOMMENDED shots only; a required one blocks like an unanswered
 *     question, and it is named for the shot, because there is no question to
 *     point at — only something to take, or a reason to write. Chip's rule for
 *     it is the same one D-017 already lived by, said in five words:
 *     *"surveyor notes why; office resolves."* The surveyor's note is the
 *     shot's OWN (`Note.shotListId`, not the section's general escape); the
 *     gap engine attaches it as `surveyorNote` (src/core/validation.ts) and it
 *     arrives here as the item's `reason`, which is what takes the row off
 *     `open` without taking it off `items` or off the record — see
 *     `Reckoning.open`.
 *
 * **A RECOMMENDED missing photo is never here.** §4 rule 8 — warn, list, never
 * block. It keeps its amber box on the close screen and its line on the
 * record; `pit.ladders` never taken produces no item on this list, and a
 * REQUIRED shot's own missed line above is not that exception loosening.
 *
 * **The locked pit is answered, not flagged.** Until D-033 a section the
 * surveyor flagged as blocked was skipped here whole — flag and unanswered
 * questions alike — because flagging was the deliberate act and the form must
 * not ask twice. D-033 R15 deleted the flag card, and round 8, task 1 retired
 * the `surveyor_flagged` gap from live results with it: a pit nobody could get
 * into is answered No / 0 and the reason lives in that section's notes, which
 * is the same door every other blockage uses. A flag still on an old record
 * is the office's to read; it skips nothing here.
 *
 * ── The surveyor never clears a gap (§2.3) ───────────────────────────────────
 * Answering the question is the only thing that makes a gap go away, because
 * that is the only thing that actually fixes it. Nothing on a phone downgrades,
 * dismisses or withdraws one; the gap travels with the record, is counted in
 * the button's caption and is rendered in the submission notification, and only
 * Chip and Tim resolve it.
 *
 * Pure: no React, no store, no clock. `tests/unit/reckoning.test.ts` runs it
 * directly.
 */

import type {
  Gap,
  IssueTally,
  SectionInstance,
  SurveyDocument,
} from '../../core/types';
import type { Route } from '../routing';
import type { SurveyResults } from './results';

/* ═════════════════════════════ the shape of it ═══════════════════════════ */

/**
 * Why this answer is outstanding — see the module header.
 *
 * D-021 gave each kind a SENTENCE, because *"outstanding"* sent the design
 * authority hunting for a blank that was never there. D-038 took the sentence
 * back off the screen — she named both of them as noise on 2026-09-03 — because
 * since D-023 the row carries the surveyor's own control, and an empty box
 * beside a Yes/No still reading No says which it is without a word. (The two
 * sentences are quoted, and banned from src/ui, in
 * `tests/unit/word-diet.test.ts`; a header that repeated them here would be the
 * words back in the file that deleted them.)
 *
 * The kind survives where it is USED: `buildReckoning` demands an untouched
 * rule and leaves a rule the surveyor answered themselves with Chip and Tim
 * (§2.3).
 *
 * `'photo'` joined in round 9, Task 4 (Amendment 8, D-041) — see the module
 * header's closing paragraph for what makes it different from the other two.
 */
export type OutstandingKind = 'unanswered' | 'untouched' | 'photo';

export interface OutstandingItem {
  /** The engine's own gap id. The row's React key is built from exactly this. */
  gapId: string;
  sectionInstanceId: string;
  /** Room-qualified, so a row reads on its own away from its screen. */
  sectionTitle: string;
  /** The first question involved. Kept for sorting and for routing. */
  questionId: string;
  /**
   * EVERY question this item is about.
   *
   * §4 rules 2 and 3 are PAIRS — replace AND refurbish — and a row that names
   * only the first sends the surveyor to fix one number and leaves the other
   * one an unexplained item on this screen. One item, both ids, so a UI renders
   * them together and fixing them together clears the row.
   */
  questionIds: string[];
  /** Spec §4 rule number when a rule is what is outstanding — 'V3'. */
  ruleId?: string;
  /** Amendment 8 (D-041) — set only on a `'photo'` row: the shot, not a question. */
  shotListId?: string;
  /**
   * Amendment 8 (D-041) — the surveyor's own written reason for a `'photo'`
   * row, off the shot's own note. Present is what takes the row off `open`
   * without taking it off `items` — see the module header.
   */
  reason?: string;
  /** The running total and the target for a counting rule. */
  tally?: IssueTally;
  kind: OutstandingKind;
  /** The question in the words it was asked in, or the rule in its own noun. */
  title: string;
  /** What is missing, in the engine's own sentence. */
  detail: string;
  /**
   * What would clear it, in the engine's own words.
   *
   * Kept on the model and no longer printed on the last look (D-038 — the
   * screen is the questions and their controls). `SectionBlocker.resolution`,
   * which this copies, is still what a §4 rule says it wants.
   */
  resolution: string;
  /** Where "Answer it" goes — the screen that asks this question. */
  route: Route;
}

export interface ReckoningSection {
  sectionInstanceId: string;
  sectionTitle: string;
  items: OutstandingItem[];
}

export interface Reckoning {
  /** Grouped by section, in walk order — the order the surveyor met them in. */
  sections: ReckoningSection[];
  items: OutstandingItem[];
  /**
   * Still overlooked. What withholds Submit.
   *
   * The same objects as `items` for a QUESTION row since D-038 — the two
   * diverged only while an acknowledgement could settle a row without
   * answering it. Amendment 8 (D-041, Task 4) reopens exactly one narrow
   * divergence: a `'photo'` item carrying a written `reason` sits in `items`
   * (the record and the count still see it) but not in `open` (Submit is not
   * withheld for it) — the surveyor's own words speaking for a shot they
   * could not take, the same door D-033 R15 gave every other blockage.
   */
  open: OutstandingItem[];
  /** True when nothing is merely overlooked. Submit may be offered. */
  settled: boolean;
}

/* ═══════════════════════════ reading the document ════════════════════════ */

/**
 * Attach every acknowledgement an OLD RECORD carries to the gap it belongs to.
 *
 * Nothing on a phone writes one any more (D-038 — see the module header), and
 * this is deliberately not a migration: a document acknowledged before today is
 * left exactly as it was, and goes on reading the way it read the day it was
 * written. Deleting this would silently drop the surveyor's words off records
 * already in the office's hands.
 *
 * The gap is returned otherwise UNCHANGED — same id, same reason, same message,
 * same blocking status. Only `surveyorNote` is added, which is the field the
 * submission notification already renders on its own line (`renderGap` in
 * src/core/notify.ts) and the close screen already shows in parentheses.
 *
 * Returns the SAME ARRAY when there is nothing to attach. Two callers depend on
 * that identity — `planFrom` hands `results.gaps` straight through, and the
 * close screen's list is the same array the button counts — and a fresh array
 * per render would also re-run every memo downstream for no reason.
 */
export function applyAcknowledgements(doc: SurveyDocument, gaps: Gap[]): Gap[] {
  const entries = doc.acknowledgedGaps;
  if (!entries || entries.length === 0) return gaps;

  const byGapId = new Map(entries.map((entry) => [entry.gapId, entry]));
  let touched = false;
  const next = gaps.map((gap) => {
    const entry = byGapId.get(gap.id);
    // An acknowledgement with no words still travels — it is the ACT that is
    // recorded — but it has no note to attach, so the gap is left alone.
    if (!entry || entry.note.trim() === '') return gap;
    touched = true;
    return { ...gap, surveyorNote: entry.note.trim() };
  });
  return touched ? next : gaps;
}

/*
 * There is no writer here, and that is the point.
 *
 * `acknowledgeGap` stood in this position until D-038. Nothing on a phone
 * records an answer as unavailable any more: the answer is No / 0 and the
 * blockage is the section's note. Adding a writer back — under any name — would
 * put a second vocabulary at the door and a second way for a survey to reach
 * the office without the answer it was asked for.
 */

/* ════════════════════════════ building the list ══════════════════════════ */

/**
 * The screen that asks this section's questions.
 *
 * Exported (2026-08-18) so the close screen's gap list can point a surveyor at
 * the section a gap belongs to — Chip stood on the close screen reading *"Car 4
 * isn't in a hoistway yet."* with nothing to tap. Same mapping the last look
 * has always used, so the two lists cannot send anyone to different screens.
 */
export function routeForSection(surveyId: string, section: SectionInstance | undefined): Route {
  if (!section) return { name: 'submit', surveyId };
  switch (section.kind) {
    case 'building':
      return { name: 'building', surveyId };
    case 'room_setup':
      return section.scope.roomId
        ? { name: 'roomSetup', surveyId, roomId: section.scope.roomId }
        : { name: 'building', surveyId };
    case 'adhoc':
      return { name: 'submit', surveyId };
    default:
      return { name: 'section', surveyId, sectionId: section.id };
  }
}

/**
 * "E1a — not answered." → "not answered."
 *
 * `detectGaps` writes an unanswered gap as `"<label> — <what is missing>"`
 * because that message has to read on its own in a Telegram message. On screen
 * the label is already the heading of the row, so repeating it doubles the
 * longest line on a 430px display. The tail is kept verbatim — including the one
 * case that is not "not answered", `"answered Yes, but the quantity is still 0"`
 * — so the surveyor and the office are told the same thing.
 */
function detailFrom(message: string, label: string): string {
  const prefix = `${label} — `;
  return message.startsWith(prefix) ? message.slice(prefix.length) : message;
}

/**
 * Everything still outstanding on a finishable survey, grouped by section.
 *
 * Reads the section blockers built from `results.gaps` — the engine's own
 * gaps, never the record's — so this cannot disagree with the list the close
 * screen prints or the count in the Submit button.
 */
export function buildReckoning(results: SurveyResults, surveyId: string): Reckoning {
  const order = new Map(results.sections.map((section, index) => [section.id, index]));

  const items: OutstandingItem[] = [];

  /*
   * THE SOURCE IS THE SECTION'S OWN BLOCKERS — D-021.
   *
   * Not a second walk over `results.gaps` deciding for itself what counts. The
   * same `SectionBlocker[]` that makes a section refuse to close, that the hall
   * indicator lamps read, and that the section header names, is what this screen
   * lists. That is the structural fix for the contradiction she hit: a section
   * cannot say "19 of 19 answered" while this screen says five are outstanding,
   * because both sentences are now written from the same objects.
   *
   * Every exclusion the module header argues for still applies, and each one is
   * still made here rather than inside `progressFor` — a blocker is a refusal to
   * CLOSE A SECTION, and this list is what withholds SUBMIT. They are different
   * questions, and §2.3 answers them differently.
   */
  for (const section of results.sections) {
    for (const blocker of results.progressBySection.get(section.id)?.blockers ?? []) {
      // Amendment 8 (D-041): a required shot names no QUESTION — there is
      // nothing to answer, only something to take — so it has to be caught
      // before the "no question, nothing to point at" skip below would drop
      // it silently.
      if (blocker.kind === 'photo' && blocker.shotListId) {
        items.push({
          gapId: blocker.gapId,
          sectionInstanceId: section.id,
          sectionTitle: blocker.sectionTitle,
          // The shot id stands where a question id would, for the sort and
          // the sticky row's key; it is dot-namespaced and can collide with
          // no catalog id.
          questionId: blocker.shotListId,
          questionIds: [],
          shotListId: blocker.shotListId,
          kind: 'photo',
          title: blocker.title,
          detail: 'required',
          resolution: '',
          route: routeForSection(surveyId, section),
          ...(blocker.surveyorNote ? { reason: blocker.surveyorNote } : {}),
        });
        continue;
      }

      // A blocker that names no question has nothing to point a surveyor at.
      // §4 rule 7 and the room-structure blocker are the cases: both are about
      // the entity tree, and room setup cannot be finished without them anyway.
      const questionId = blocker.questionIds[0];
      if (!questionId) continue;

      let kind: OutstandingKind;
      if (blocker.kind === 'unanswered') {
        kind = 'unanswered';
      } else {
        // A rule. Only when the surveyor never touched the answer — see the
        // module header. An answer they wrote themselves is theirs, the live
        // chip already told them the sum was wrong, and §2.3 leaves the
        // decision with Chip and Tim.
        if (!blocker.untouchedQuestionIds.includes(questionId)) continue;
        kind = 'untouched';
      }

      items.push({
        gapId: blocker.gapId,
        sectionInstanceId: section.id,
        sectionTitle: blocker.sectionTitle,
        questionId,
        questionIds: [...blocker.questionIds],
        ...(blocker.ruleId ? { ruleId: blocker.ruleId } : {}),
        ...(blocker.tally ? { tally: blocker.tally } : {}),
        kind,
        title: blocker.title,
        detail: detailFrom(blocker.message, blocker.title),
        resolution: blocker.resolution,
        route: routeForSection(surveyId, section),
      });
    }
  }

  items.sort((a, b) => {
    const byOrder =
      (order.get(a.sectionInstanceId) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(b.sectionInstanceId) ?? Number.MAX_SAFE_INTEGER);
    return byOrder !== 0 ? byOrder : a.questionId.localeCompare(b.questionId);
  });

  const sections: ReckoningSection[] = [];
  for (const item of items) {
    const last = sections[sections.length - 1];
    if (last && last.sectionInstanceId === item.sectionInstanceId) last.items.push(item);
    else
      sections.push({
        sectionInstanceId: item.sectionInstanceId,
        sectionTitle: item.sectionTitle,
        items: [item],
      });
  }

  // Answering is what takes a QUESTION off this list (D-038). A required
  // PHOTO (Amendment 8) leaves it two ways — the photo, which removes the
  // item, or the surveyor's written reason, which speaks for it and travels
  // with the gap. `items` and `open` differ by exactly those rows.
  const open = items.filter((item) => !(item.kind === 'photo' && item.reason));
  return { sections, items, open, settled: open.length === 0 };
}

/* ═══════════════════════════════ the copy ════════════════════════════════ */

/**
 * The headline on the last look.
 *
 * Deliberately not an error and deliberately not a count of everything wrong.
 * The surveyor has walked the whole job; this is the end of the walk, and it
 * says how much of it is left rather than how much of it is broken.
 */
export function lastLookSummary(reckoning: Reckoning): string {
  const open = reckoning.open.length;
  if (open === 1) return 'One answer is still outstanding.';
  return `${String(open)} answers are still outstanding.`;
}
